import { CertificationGoalContext } from '../../../../../../certification-goal-context';
import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Controller, FormProvider, useForm} from "react-hook-form";

import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import {Button, FormControl, InputLabel } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Search as SearchIcon } from '@vedmarket/ui-kit';
import { Delete as DeleteIcon } from '@vedmarket/ui-kit';

import CertificationCodeCuTrOption from "../cu-tr-option";
import {CERTIFICATION_AUTO_FORM} from "../../../../index";
import {
  useCertificationSchemes,
  useLazyGetCertificationSchemesQuery,
} from "../../../../../../../../../../../api/certification_schemes";
import {useDeclarationTypes} from "../../../../../../../../../../../api/declaration_types";
import {useDocumentDurations} from "../../../../../../../../../../../api/document_durations";
import {
  getHscodeDataMutation,
  removeBrokerDataMutation,
  setBrokerDataMutation,
  useBaseCurrency,
  useLazyGetBrokerDataByCodeQuery,
  useSetCompletedByOrderIdMutation
} from "../../../../../../../../../../../api/calc";
import {COLORS, CERTIFICATION_DECLARATION_TYPE} from 'config/constants';
import {useGetCalcIdFromRoute} from "../../../../../../../../../../../utils/hooks";
import {formatHSCode} from "../../../../../../../../../../../utils/helpers";
import ModalSearch from "../../../modalSearch";
import ModalBig from "../../../../../../../../../../common/modalBig";
import CompanyCard from "../../../company-card";

import './cu-tr.scss';
import {LinkPopover} from '@vedmarket/ui-kit';
import '../../certification-help.scss';
import CertificationCodeNotFound from "../cu-tr-not-found";
import {NotificationWarning} from "./components/notification-warning";
import {NotificationInfo} from "./components/notification-info";
import CertificationNoNeeded from "./components/notification-no-needed";
import {BROKER_ACTIONS} from "../../../../../../../../../../../utils/constants";
import {isCalculationViewModeError} from 'utils/helpers';
import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../../../../../../../calculation-read-only';

const helpListStyles = {
  margin: '8px 0 0',
  paddingLeft: '24px',
};

const CertificationCodeCuTr = ({index, documentType, countryCode, code} : CertificationCodeCuTrProps) => {

  const { t } = useTranslation()
  const readOnly = useCalculationReadOnly()
  const { report } = React.useContext(CertificationGoalContext);

  const declarationTypes = useDeclarationTypes();
  const documentDurations = useDocumentDurations();

  const [ selectedGroup, setSelectedGroup ] = useState<CertificationSelectedGroupItem[]>([]);
  const [ centers, setCenters ] = React.useState<HsCodeOutput>();
  const [ notFoundDescription, setNotFoundDescription ] = React.useState(false);

  const [ removeBroker ] = removeBrokerDataMutation()
  const [ setCompleted ] = useSetCompletedByOrderIdMutation()
  const [ setBrokerData ] = setBrokerDataMutation()

  const [ certificationSchemesRequest, schemesRequest ] = useLazyGetCertificationSchemesQuery()
  const { isSuccess, isFetching, originalArgs } = schemesRequest;
  const [ triggerGetBrokerDataByCode ] = useLazyGetBrokerDataByCodeQuery();

  useEffect(() => {
    setNotFoundDescription(documentType?.no_need_certificate === 'true');
  }, [ documentType?.no_need_certificate ])

  const getDocumentsByGroup = () => {
    const docs: DocumentsByCodeItemsDocs[] = [];

    selectedGroup?.map((value: CertificationSelectedGroupItem) => {
      const group = documentType.items.find((documentItem) => documentItem.group.code === value.code);
      if (group) {
        docs.push(group.docs);
      }
    })

    if (!docs.length) {
      return undefined;
    }

    return docs.reduce(
      (acc, item) => {
        Object.entries(item.required).forEach(([ key, value ]) => {
          acc.required[key] = value;
        });

        Object.entries(item.variant).forEach(([ key, value ]) => {
          acc.variant[key] = value;
        });

        return acc;
      },
      { required: {} as Record<string, string>, variant: {} as Record<string, string> }
    );

  };

  const getDocsValues = (documents: DocumentsByCodeItemsDocs)=> {
    const allKeys = [
      ...Object.keys(documents.required || {}),
      ...Object.keys(documents.variant || {})
    ];

    const uniqueKeys = allKeys.filter((key, index) => allKeys.indexOf(key) === index);

    return uniqueKeys.join(',');
  }

  const documents = getDocumentsByGroup();
  const docs = documents ? getDocsValues(documents) : '';
  const certificationSchemes = useCertificationSchemes({ trts: documentType.code, docs });

  const [ shouldOpenModal, setShouldOpenModal ] = useState(false)

  const [ getHscodeData, { isLoading: isSearching } ] = getHscodeDataMutation()

  const methods = useForm<CertificationCodeCuTrForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      declaration_type: '',
      certification_scheme: '',
      document_duration: '',
    },
  });

  const { setValue, control, watch, clearErrors, handleSubmit } = methods;

  const values = watch();
  const isBatch = values.declaration_type === CERTIFICATION_DECLARATION_TYPE.BATCH;
  const schemesMatchDocuments = originalArgs?.trts === documentType.code && originalArgs?.docs === docs;
  const hasSchemesResponse = isSuccess && schemesMatchDocuments;
  const hasNoSchemes = hasSchemesResponse && certificationSchemes?.length === 0;
  const schemesAreReady = hasSchemesResponse && !isFetching;
  const hasSelectedScheme = certificationSchemes?.some((item) => item.code === values.certification_scheme);
  const hasValidScheme = hasNoSchemes || hasSelectedScheme;
  const hasValidDuration = isBatch || Boolean(values.document_duration);
  useEffect(() => {
    const reportDocument = () => {
      const fields = methods.getValues();
      const showSearchFields = !notFoundDescription && selectedGroup.length >= 1;
      const center = documentType.brokerCenter;
      const snapshot = center ? {
        company: center.company?.name,
        cost_total: center.cost_total,
      } : {
        groups: documentType.items.map((item, groupIndex) => ({
          name: item.group.name,
          selected: selectedGroup.some((group) => group.code === item.group.code && group.index === groupIndex),
        })),
        no_need_certificate: notFoundDescription,
        ...(showSearchFields ? {
          declaration_type: fields.declaration_type,
          ...(!hasNoSchemes ? { certification_scheme: fields.certification_scheme } : {}),
          ...(!isBatch ? { document_duration: fields.document_duration } : {}),
        } : {}),
      };

      report({ [`${code}_${documentType.code}`]: { document: documentType.name, ...snapshot } });
    };

    reportDocument();
    const subscription = watch(reportDocument);

    return () => {
      subscription.unsubscribe();
      report({ [`${code}_${documentType.code}`]: undefined });
    };
  }, [ report, watch, methods, code, documentType, notFoundDescription, selectedGroup, hasNoSchemes, isBatch ]);

  const hasRequiredFields = Boolean(values.declaration_type) && hasValidScheme;
  const canSearch = hasRequiredFields && hasValidDuration;
  const searchIsUnavailable = !canSearch || !schemesAreReady;
  const isSearchDisabled = searchIsUnavailable || isSearching;

  useEffect(() => {
    setValue(CERTIFICATION_AUTO_FORM.CERTIFICATION_SCHEME, '', { shouldValidate: true });

    if (docs) {
      certificationSchemesRequest({ trts: documentType.code, docs });
    }
  }, [ docs, documentType.code, certificationSchemesRequest, setValue ])

  useEffect(() => {
    if (hasNoSchemes) {
      clearErrors(CERTIFICATION_AUTO_FORM.CERTIFICATION_SCHEME);
    }

    if (isBatch) {
      setValue(CERTIFICATION_AUTO_FORM.DOCUMENT_DURATION, '');
      clearErrors(CERTIFICATION_AUTO_FORM.DOCUMENT_DURATION);
    }
  }, [ hasNoSchemes, isBatch, clearErrors, setValue ])

  const orderId = useGetCalcIdFromRoute()

  const baseCurrency = useBaseCurrency();

  const handleClose = () => setShouldOpenModal(false);

  const handleChangeSelectedGroup = (index: number, code: string) => {
    if (readOnly) {
      return;
    }

    setSelectedGroup((prev) => {
      const exists = prev.some((item) => item.code === code && item.index === index);

      if (exists) {
        return prev.filter((item) => !(item.code === code && item.index === index));
      } else {
        return [ ...prev, { code, index } ];
      }
    });
  };

  const shouldShowWarning = !selectedGroup?.length;

  const optionsCertificationSchemes = certificationSchemes?.map((item: CertificationSchemesItem) => ({
    value: item.code,
    label: item.name,
  }));

  const optionsDeclarationTypes = declarationTypes?.map((item: CertificationSchemesItem) => ({
    value: item.code,
    label: item.name,
  }));

  const optionsDocumentDurations = documentDurations?.map((item: CertificationSchemesItem) => ({
    value: item.code,
    label: item.name,
  }));

  const handleSearch = handleSubmit(async values => {
    if (isSearchDisabled) {
      return;
    }

    const getTechReglament = (): HsCodeInputCodeParamsTechReglamentItem[] => {
      const toItem = (index: number): HsCodeInputCodeParamsTechReglamentItem => {
        const group = documentType.items[index];
        return {
          code: documentType.code,
          group_code: group?.group.id,
          docs: group?.docs,
        };
      };

      return selectedGroup.map((groupCode) => toItem(groupCode.index));
    };

    const techReglaments = getTechReglament();
    const shouldSendScheme = !hasNoSchemes && Boolean(values.certification_scheme);

    const codeParams: HsCodeInputCodeParams = {
      code: code,
      country_code: countryCode,
      declaration_type: values.declaration_type,
      ...(shouldSendScheme ? { certification_scheme: values.certification_scheme } : {}),
      ...(values.document_duration ? { document_duration: values.document_duration } : {}),
      tech_reglaments: techReglaments,
    }

    const result = await getHscodeData({
      orderId: orderId,
      code_params: codeParams,
      currency: baseCurrency,
    });

    if (!('data' in result)) {
      return;
    }

    setCenters(result.data);
    setShouldOpenModal(true);
  })

  const handleRemove = async () => {
    const result = await removeBroker({
      orderId: orderId,
      code: code,
      countryCode: countryCode,
      tech_reglament: documentType.code
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    await setCompleted({
      orderId,
      certification: false,
      banking_commissions: false,
      additional_services: false
    })
    triggerGetBrokerDataByCode(orderId);
  }

  const handleNoNeedCertificate = async () => {
    if (readOnly) {
      return;
    }

    setNotFoundDescription(!notFoundDescription);
    if (notFoundDescription){
      await handleRemove();
    } else {
      const result = await setBrokerData({
        orderId,
        code: code,
        has_certificate: null,
        countryCode: countryCode,
        tech_reglament: documentType.code,
        action: BROKER_ACTIONS.CERT_FIND_MISSING_PRODUCT
      });

      if (isCalculationViewModeError(result)) {
        return;
      }

      triggerGetBrokerDataByCode(orderId);
    }
  }

  return <>
    <div className='cu-tr'>
      <Typography variant='subtitle1' sx={{
        fontWeight: 500,
        lineHeight: '24px',
        color: COLORS.BLACK_DARK,
      }}>
        {index + 1}. {formatReferenceCopy(documentType.name)}
      </Typography>
      <Typography variant='body2' sx={{
        marginTop: '8px',
        lineHeight: '21px',
        color: COLORS.LIGHT_TEXT_SECONDARY,
      }}>
        {formatReferenceCopy(documentType.title)}
      </Typography>
      <div className='cu-tr__help'>
        <LinkPopover
          linkText={t('calc__help__certificationRegulationLink')}
          text={<div className='certification-help'>
            <Typography variant='inherit'>
              {formatReferenceCopy(t('calc__help__certificationRegulationIntro'))}
            </Typography>
            <Typography variant='inherit'>
              {formatReferenceCopy(t('calc__help__certificationRegulationSelection'))}
            </Typography>
          </div>}
        />
      </div>
      <Divider sx={{
        marginBottom: 0,
        marginTop: '24px',
      }}/>
      {!documentType.brokerCenter && <>
        {documentType.items.map((item, index) =>
          <CertificationCodeCuTrOption
            item={item}
            key={index}
            index={index}
            handleChangeSelectedGroup={handleChangeSelectedGroup}
            code={documentType.code}
            isDisabled={notFoundDescription || readOnly}
            isActive={selectedGroup.some(
              (selected) =>
                selected.code === item.group.code && selected.index === index
            )}
          />)}
        <CertificationCodeNotFound
          isActive={notFoundDescription}
          disabled={readOnly}
          onChange={handleNoNeedCertificate}
        />
        {!notFoundDescription && shouldShowWarning && <NotificationWarning code={documentType.name}/>}
        {!notFoundDescription && documents && <NotificationInfo documents={documents}/>}
        {notFoundDescription && <CertificationNoNeeded documentName={documentType.name}/> }
        {!notFoundDescription && selectedGroup.length >= 1 && <div className='certification-code__search'>
          <FormControl className='certification-code__declarationType' sx={{
            gap: '8px',
          }}>
            <InputLabel
              id='declarationType'
              shrink
            >
              <Typography component='span' variant='inherit'>
                {t('dictionaries__text__certification__typeOfDeclaration')}
              </Typography>
            </InputLabel>
            <Controller
              control={control}
              name={CERTIFICATION_AUTO_FORM.DECLARATION_TYPE}
              rules={{ required: t('common__text__isRequired') as string }}
              render={({ field, fieldState }) => (
                <Select
                  notched
                  disabled={readOnly}
                  labelId='declarationType'
                  id='declarationType'
                  value={field.value ?? ''}
                  label={t('dictionaries__text__certification__typeOfDeclaration')}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                >
                  {optionsDeclarationTypes?.map((option: CertificationCodeCuTrOptionItem) => (
                    <MenuItem value={option.value} key={option.value}>
                      <Typography component='span' variant='inherit'>{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <LinkPopover
              linkText={t('calc__help__certificationTypeLink')}
              text={<div className='certification-help'>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationTypeIntro'))}
                </Typography>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationTypeBatch'))}
                </Typography>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationTypeSeries'))}
                </Typography>
              </div>}
            />
          </FormControl>
          <FormControl fullWidth disabled={!schemesAreReady || hasNoSchemes} sx={{
            gap: '8px',
          }}>
            <InputLabel
              id='certificationScheme'
              shrink>
              <Typography component='span' variant='inherit'>
                {t('dictionaries__text__certification__certificationScheme')}
              </Typography>
            </InputLabel>
            <Controller
              control={control}
              name={CERTIFICATION_AUTO_FORM.CERTIFICATION_SCHEME}
              rules={{ required: !hasNoSchemes && t('common__text__isRequired') as string }}
              render={({ field }) => (
                <Select
                  notched
                  disabled={readOnly}
                  labelId='certificationScheme'
                  id='certificationScheme'
                  value={field.value ?? ''}
                  label={t('dictionaries__text__certification__certificationScheme')}
                  displayEmpty
                  renderValue={(value) => (
                    <Typography variant='body1'>
                      {hasNoSchemes
                        ? t('dictionaries__text__certification__noSchemes')
                        : optionsCertificationSchemes?.find((option) => option.value === value)?.label}
                    </Typography>
                  )}
                  onChange={field.onChange}
                >
                  {optionsCertificationSchemes?.map((option: CertificationCodeCuTrOptionItem) => (
                    <MenuItem value={option.value} key={option.value}>
                      <Typography component='span' variant='inherit'>{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <LinkPopover
              linkText={t('calc__help__certificationSchemeLink')}
              text={<div className='certification-help'>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationSchemeIntro'))}
                </Typography>
                <div>
                  <Typography variant='inherit'>
                    {formatReferenceCopy(t('calc__help__certificationSchemeBatch'))}
                  </Typography>
                  <Typography component='ul' variant='inherit' sx={helpListStyles}>
                    <Typography component='li' variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationSchemeBatchItem1'))}
                    </Typography>
                    <Typography component='li' variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationSchemeBatchItem2'))}
                    </Typography>
                  </Typography>
                </div>
                <div>
                  <Typography variant='inherit'>
                    {formatReferenceCopy(t('calc__help__certificationSchemeSeries'))}
                  </Typography>
                  <Typography component='ul' variant='inherit' sx={helpListStyles}>
                    <Typography component='li' variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationSchemeSeriesItem1'))}
                    </Typography>
                    <Typography component='li' variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationSchemeSeriesItem2'))}
                    </Typography>
                    <Typography component='li' variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationSchemeSeriesItem3'))}
                    </Typography>
                    <Typography component='li' variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationSchemeSeriesItem4'))}
                    </Typography>
                  </Typography>
                </div>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationSchemePreliminary'))}
                </Typography>
              </div>}
            />
          </FormControl>
          <FormControl fullWidth disabled={isBatch || readOnly} sx={{
            gap: '8px',
          }}>
            <InputLabel
              id='certificateDuration'
              shrink>
              <Typography component='span' variant='inherit'>
                {t('dictionaries__text__certification__termOfTheCertificate')}
              </Typography>
            </InputLabel>
            <Controller
              control={control}
              name={CERTIFICATION_AUTO_FORM.DOCUMENT_DURATION}
              rules={{ required: !isBatch && t('common__text__isRequired') as string }}
              render={({ field, fieldState }) => (
                <Select
                  disabled={readOnly || isBatch}
                  labelId='certificateDuration'
                  id='certificateDuration'
                  value={field.value ?? ''}
                  label={t('dictionaries__text__certification__termOfTheCertificate')}
                  notched
                  displayEmpty
                  renderValue={(value) => (
                    <Typography variant='body1'>
                      {isBatch
                        ? t('dictionaries__text__certification__durationNotRequired')
                        : optionsDocumentDurations?.find((option) => option.value === value)?.label}
                    </Typography>
                  )}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                >
                  {optionsDocumentDurations?.map((option: CertificationCodeCuTrOptionItem) => (
                    <MenuItem value={option.value} key={option.value}>
                      <Typography component='span' variant='inherit'>{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <LinkPopover
              linkText={t('calc__help__certificationDurationLink')}
              text={<div className='certification-help'>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationDurationIntro'))}
                </Typography>
                <Typography variant='inherit'>
                  {formatReferenceCopy(t('calc__help__certificationDurationSeries'))}
                </Typography>
              </div>}
            />
          </FormControl>
          <Button
            variant='outlined'
            fullWidth
            className={cx('certification-code__searchButton', { 'read-only-action-button': readOnly })}
            startIcon={<SearchIcon />}
            endIcon={readOnlyButtonEndIcon(readOnly)}
            onClick={readOnly ? undefined : handleSearch}
            disabled={readOnly || isSearchDisabled}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
          >
            <Typography component='span' variant='inherit'>
              {t('dictionaries__text__certification__findOptionsForTheCostOfCertification')}
            </Typography>
          </Button>
        </div>}
      </>}
      {documentType.brokerCenter && <div className='certification-code__currentBroker'>
        <CompanyCard
          // @ts-ignore
          center={documentType.brokerCenter}
          code={code}
          isActive
        />
        <Button
          variant='contained'
          fullWidth
          className={cx({ 'read-only-action-button': readOnly })}
          disabled={readOnly}
          startIcon={<DeleteIcon />}
          endIcon={readOnlyButtonEndIcon(readOnly)}
          onClick={readOnly ? undefined : handleRemove}
          title={readOnly ? t('calc__message__lockedByApplication') : undefined}
        >
          <Typography component='span' variant='inherit'>
            {t('dictionaries__text__certification__resetTheCostOfCertification')}
          </Typography>
        </Button>
      </div>}
    </div>
    {shouldOpenModal && <ModalBig
      isOpen={Boolean(shouldOpenModal)}
      onClose={handleClose}
      title={
        <Typography component='span' variant='inherit'>
          {t('dictionaries__text__certification__searchForCertificationServices')}{' '}
          {code ? `${t('declension__text__HSCodeSeveral')} ${formatHSCode(code)} (${countryCode})`  : ''}
        </Typography>
      }
      isSearch
    >
      <FormProvider {...methods}>
        <ModalSearch
          preloadCenters={centers}
          handleClose={handleClose}
          code={code}
          countryCode={countryCode}
          techReglament={documentType.code}
        />
      </FormProvider>
    </ModalBig>}
  </>
}

export default CertificationCodeCuTr
